import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Input, Button, Alert, Spin, Typography, Space, Divider, Card } from "antd";
import { RobotOutlined, CheckOutlined } from "@ant-design/icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { agentGovernanceApi } from "../../api/v3/agentGovernanceApi";

const { TextArea } = Input;
const { Text } = Typography;

const extractReviewCaseInfo = (reviewCase) => {
    if (!reviewCase?.id) {
        return null;
    }

    return {
        review_case_id: reviewCase.id,
        proposal_revision_id: reviewCase.proposal_revision_id,
        payload_hash: reviewCase.payload_hash,
        grounding_snapshot_hash: reviewCase.grounding_snapshot_hash,
        review_payload_hash: reviewCase.review_payload_hash,
    };
};

const commitDecision = (reviewCaseInfo, decisionType, decisionPayload = {}) => {
    if (!reviewCaseInfo?.review_case_id) {
        return;
    }

    agentGovernanceApi
        .submitInlineReviewCaseDecision(reviewCaseInfo.review_case_id, {
            proposal_revision_id: reviewCaseInfo.proposal_revision_id,
            proposal_payload_hash: reviewCaseInfo.payload_hash,
            grounding_snapshot_hash: reviewCaseInfo.grounding_snapshot_hash,
            review_payload_hash: reviewCaseInfo.review_payload_hash,
            decision_type: decisionType,
            decision_payload: decisionPayload,
        })
        .catch((err) => {
            console.warn("提交AI填写审计决策失败:", err);
        });
};

/**
 * AI智能填写表单Modal组件
 * 通用组件，可用于任何表单的AI辅助填写
 *
 * 切换至 AG v3 同步 inline 通道后：
 * - AI 填写请求走 `POST /v3/ag/requests/master-data/form-fill`（同步返回 review_case + proposal）
 * - 用户采纳/放弃/重新解析时，对对应 review_case 提交 inline decision（审计动作，失败不阻塞 UX）
 */
const AIFormFillModal = ({
    visible,
    onCancel,
    onSuccess,
    formType,
    formTypeLabel,
    currentFormData = {},
    operationType = "CREATE", // CREATE 或 UPDATE
    title = "AI智能填写表单",
}) => {
    const displayFormType = formTypeLabel || formType;
    // 状态持久化的key（v2 标识，避免与旧 v1 草稿/旧 review_case_id 混用）
    const persistenceKey = `ai-form-fill-v2-${formType}-${operationType}`;

    const [userInput, setUserInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [step, setStep] = useState("input"); // input, processing, result
    const pendingReviewCaseRef = useRef(null);

    // 保存状态到localStorage
    const saveState = (state) => {
        try {
            localStorage.setItem(persistenceKey, JSON.stringify(state));
        } catch (error) {
            console.warn("保存AI填写状态失败:", error);
        }
    };

    // 从localStorage加载状态
    const loadState = useCallback(() => {
        try {
            const saved = localStorage.getItem(persistenceKey);
            return saved ? JSON.parse(saved) : null;
        } catch (error) {
            console.warn("加载AI填写状态失败:", error);
            return null;
        }
    }, [persistenceKey]);

    // 清除保存的状态
    const clearState = useCallback(() => {
        try {
            localStorage.removeItem(persistenceKey);
        } catch (error) {
            console.warn("清除AI填写状态失败:", error);
        }
    }, [persistenceKey]);

    // 重置状态
    const resetState = () => {
        const pendingReviewCase = pendingReviewCaseRef.current;
        if (pendingReviewCase) {
            pendingReviewCaseRef.current = null;
            commitDecision(pendingReviewCase, "ignore", { reason: "state_reset" });
        }

        setUserInput("");
        setResult(null);
        setError(null);
        setStep("input");
        clearState();
    };

    // 当Modal打开时加载之前的状态
    useEffect(() => {
        if (visible) {
            const savedState = loadState();
            if (savedState) {
                setUserInput(savedState.userInput || "");
                setResult(savedState.result || null);
                setError(savedState.error || null);
                setStep(savedState.step || "input");
            } else {
                setUserInput("");
                setResult(null);
                setError(null);
                setStep("input");
            }
            pendingReviewCaseRef.current = null;
        }
        // 注意：这里不在Modal关闭时清空状态，让用户可以重新打开查看
    }, [visible, persistenceKey, loadState]); // 移除clearState依赖

    // 调用AI填写API
    const handleAIFill = async () => {
        if (!userInput.trim()) {
            setError("请输入要解析的文本内容");
            return;
        }

        const previousReviewCase = pendingReviewCaseRef.current;
        if (previousReviewCase) {
            pendingReviewCaseRef.current = null;
            commitDecision(previousReviewCase, "reject", { reason: "reparse_requested" });
        }

        setLoading(true);
        setError(null);
        setStep("processing");

        try {
            const response = await agentGovernanceApi.createMasterDataFormFillRequest({
                form_type: formType,
                operation_type: operationType,
                user_input: userInput,
                current_form_data: currentFormData,
            });

            pendingReviewCaseRef.current = extractReviewCaseInfo(response.data.review_case);

            const normalizedResult = {
                formData: response.data.form_data,
                executionInfo: response.data.execution_info,
            };

            setResult(normalizedResult);
            setStep("result");
            // 保存成功状态；review_case 是一次性审计对象，不持久化。
            saveState({
                userInput,
                result: normalizedResult,
                error: null,
                step: "result",
            });
        } catch (err) {
            console.error("AI填写失败:", err);
            const errorMessage = err.response?.data?.detail || "网络错误，请稍后重试";
            setError(errorMessage);
            setStep("input");
            // 保存错误状态
            saveState({
                userInput,
                result: null,
                error: errorMessage,
                step: "input",
            });
        } finally {
            setLoading(false);
        }
    };

    // 应用AI解析结果
    const handleApplyResult = () => {
        if (result?.formData) {
            const pendingReviewCase = pendingReviewCaseRef.current;
            pendingReviewCaseRef.current = null;
            onSuccess(result.formData);
            commitDecision(pendingReviewCase, "accept_as_is", { form_data: result.formData });
            onCancel(); // 关闭Modal
        }
    };

    const handleClose = () => {
        const pendingReviewCase = pendingReviewCaseRef.current;
        if (pendingReviewCase) {
            pendingReviewCaseRef.current = null;
            commitDecision(pendingReviewCase, "ignore", { reason: "modal_closed" });
        }
        onCancel();
    };

    // 生成用户友好的Markdown预览
    const generateMarkdownPreview = (formData) => {
        if (!formData || typeof formData !== "object") {
            return "暂无解析结果";
        }

        // 字段名称映射（英文 -> 中文）
        const fieldNameMap = {
            route_name: "航路名称",
            departure_airport: "出发机场",
            arrival_airport: "到达机场",
            route_waypoints: "航路点序列",
            block_time_minutes: "飞行时间（分钟）",
            distance_nm: "距离（海里）",
            nautical_miles: "航程海里数",
            average_wind_component: "平均风分量",
            wind_months: "几月风",

            // 燃油规划字段
            trip_fuel: "航程燃油/BURN（公斤）",
            fuel_over_destination: "目的地上空燃油/FOD（公斤）",
            contingency_fuel: "应急燃油（公斤）",
            alternate_fuel: "备降燃油（公斤）",
            final_reserve_fuel: "最终储备燃油（公斤）",
            additional_fuel: "附加燃油（公斤）",
            taxi_fuel: "滑行燃油（公斤）",
            block_fuel: "所需燃油/Required Fuel（吨）",

            // 载荷规划字段
            passenger_count: "旅客数量/PAX",
            cargo_weight: "货物重量/CARGO（吨）",
            total_traffic_load: "总业载/TTL（公斤）",

            // 重量与性能字段
            operating_empty_weight: "基本空重（吨）",
            max_takeoff_weight: "最大起飞重量（吨）",
            max_landing_weight: "最大着陆重量（吨）",

            // 其他字段
            alternate_airports: "备用机场",
            overflight_countries: "过境国家/地区",
            route_type: "航路类型",
            weather_category: "天气类别",
            is_default: "默认航路",
            remarks: "备注",
            tags: "标签",
        };

        let markdown = "";

        // 定义字段显示顺序（按照航空业务逻辑排序）
        const fieldOrder = [
            // 基本信息
            "route_name",
            "departure_airport",
            "arrival_airport",
            "alternate_airports",
            "block_time_minutes",
            "block_fuel",
            "distance_nm",
            "nautical_miles",
            "average_wind_component",
            "wind_months",
            "route_waypoints",
            // 燃油规划（按重要性排序）
            "trip_fuel",
            "fuel_over_destination",
            "contingency_fuel",
            "alternate_fuel",
            "final_reserve_fuel",
            "additional_fuel",
            "taxi_fuel",
            // 载荷规划
            "passenger_count",
            "cargo_weight",
            "total_traffic_load",
            // 重量与性能
            "operating_empty_weight",
            "max_takeoff_weight",
            "max_landing_weight",
            // 其他信息
            "overflight_countries",
            "route_type",
            "weather_category",
            "is_default",
            "remarks",
            "tags",
        ];

        // 按顺序显示字段
        fieldOrder.forEach((key) => {
            if (formData.hasOwnProperty(key)) {
                const value = formData[key];
                const fieldName = fieldNameMap[key] || key;
                let displayValue = value;

                // 特殊值处理
                if (typeof value === "boolean") {
                    displayValue = value ? "是" : "否";
                } else if (Array.isArray(value)) {
                    displayValue = value.join(", ");
                } else if (value === null || value === undefined) {
                    displayValue = "未设置";
                } else if (typeof value === "number") {
                    // 数值格式化处理
                    if (key === "block_fuel") {
                        // Required Fuel 显示为吨，保留1位小数
                        displayValue = `${value.toFixed(1)}T`;
                    } else if (key.includes("fuel") || key === "total_traffic_load") {
                        // 其他燃油字段和TTL显示为公斤，添加千位分隔符
                        displayValue = value.toLocaleString() + (key.includes("fuel") ? "kg" : "kg");
                    } else if (key === "cargo_weight" || key.includes("weight")) {
                        // 重量字段显示为吨，保留2位小数
                        displayValue = `${value.toFixed(2)}T`;
                    } else if (key === "block_time_minutes") {
                        // 时间转换为小时:分钟格式
                        const hours = Math.floor(value / 60);
                        const minutes = value % 60;
                        displayValue = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
                    } else {
                        // 其他数值保持原样
                        displayValue = value.toLocaleString();
                    }
                }

                markdown += `**${fieldName}**: ${displayValue}\n\n`;
            }
        });

        // 显示未在顺序列表中的其他字段
        Object.entries(formData).forEach(([key, value]) => {
            if (!fieldOrder.includes(key)) {
                const fieldName = fieldNameMap[key] || key;
                let displayValue = value;

                // 特殊值处理
                if (typeof value === "boolean") {
                    displayValue = value ? "是" : "否";
                } else if (Array.isArray(value)) {
                    displayValue = value.join(", ");
                } else if (value === null || value === undefined) {
                    displayValue = "未设置";
                }

                markdown += `**${fieldName}**: ${displayValue}\n\n`;
            }
        });

        return markdown;
    };

    // 渲染输入步骤
    const renderInputStep = () => (
        <div>
            <Alert
                message="AI智能填写说明"
                description={
                    <div>
                        <p>请粘贴或输入包含表单信息的原始文本，AI将自动解析并填写表单字段。</p>
                        <p>
                            <strong>支持的格式：</strong>任意格式的文本、表格数据、结构化信息等
                        </p>
                        <p>
                            <strong>操作模式：</strong>
                            {operationType === "CREATE" ? "创建新记录" : "更新现有记录"}
                        </p>
                    </div>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text strong>请输入要解析的文本内容：</Text>
                {(userInput || result) && (
                    <Button size="small" type="link" onClick={resetState} style={{ padding: 0 }}>
                        清除历史记录
                    </Button>
                )}
            </div>

            <TextArea
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder={`请粘贴包含${displayFormType}信息的文本内容...

例如：
航路名称：PEK-SHA-01
出发机场：PEK（北京首都国际机场）
到达机场：SHA（上海虹桥国际机场）
航路点序列：PEK BOBAK DUMET PIKAS SHA
飞行时间：2小时15分钟
...`}
                rows={12}
                style={{ marginBottom: 16 }}
            />

            {error && <Alert message="解析失败" description={error} type="error" showIcon style={{ marginBottom: 16 }} />}
        </div>
    );

    // 渲染处理步骤
    const renderProcessingStep = () => (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
                <Text>AI正在解析您的文本内容，请稍候...</Text>
            </div>
        </div>
    );

    // 渲染结果步骤
    const renderResultStep = () => {
        if (!result || !result.formData) return null;

        const formData = result.formData;
        const executionInfo = result.executionInfo;

        return (
            <div>
                <Alert message="AI解析完成" description={`成功解析了 ${Object.keys(formData || {}).length} 个字段`} type="success" showIcon style={{ marginBottom: 16 }} />

                {executionInfo && (
                    <Card size="small" style={{ marginBottom: 16 }}>
                        <Text type="secondary">
                            执行时间: {executionInfo.duration?.duration_seconds ? `${executionInfo.duration.duration_seconds}秒` : "未知"} | 执行ID: {executionInfo.execution_id?.substring(0, 8)}...
                        </Text>
                    </Card>
                )}

                {/* 原始文本和解析结果对比显示 - 一行布局 */}
                <div style={{ display: "flex", gap: "16px", marginBottom: 16 }}>
                    {/* 左侧：原始文本 */}
                    <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 8 }}>
                            <Text strong>原始文本内容：</Text>
                        </div>
                        <Card size="small" style={{ background: "#fafafa" }}>
                            <TextArea value={userInput} onChange={(e) => setUserInput(e.target.value)} rows={10} placeholder="您可以在这里修改原始文本内容..." style={{ resize: "none", border: "none", background: "transparent" }} />
                        </Card>
                    </div>

                    {/* 右侧：解析结果 */}
                    <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: 8 }}>
                            <Text strong>AI解析结果：</Text>
                        </div>
                        <Card size="small" style={{ background: "#f0f9ff", border: "1px solid #e0f2fe" }}>
                            <div
                                style={{
                                    maxHeight: "300px",
                                    overflow: "auto",
                                    padding: "8px",
                                }}
                            >
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{generateMarkdownPreview(formData)}</ReactMarkdown>
                            </div>
                        </Card>
                    </div>
                </div>

                <Divider />

                <Alert
                    message="下一步操作"
                    description={
                        <div>
                            <p>请确认解析结果是否正确。</p>
                            <p>
                                • 点击<strong>"应用到表单"</strong>将自动填写表单字段
                            </p>
                            <p>
                                • 点击<strong>"重新编辑"</strong>可以微调原始文本内容，然后重新解析
                            </p>
                            <p>• 下次打开此窗口时，会自动恢复当前的解析状态</p>
                        </div>
                    }
                    type="info"
                    showIcon
                />
            </div>
        );
    };

    // 渲染底部按钮
    const renderFooter = () => {
        if (step === "input") {
            return [
                <Button key="cancel" onClick={handleClose}>
                    取消
                </Button>,
                <Button key="submit" type="primary" icon={<RobotOutlined />} loading={loading} onClick={handleAIFill} disabled={!userInput.trim()}>
                    AI解析
                </Button>,
            ];
        }

        if (step === "processing") {
            return [
                <Button key="cancel" onClick={handleClose} disabled={loading}>
                    取消
                </Button>,
            ];
        }

        if (step === "result") {
            return [
                <Button key="clear" onClick={resetState}>
                    清空
                </Button>,
                <Button key="reParse" icon={<RobotOutlined />} onClick={handleAIFill} loading={loading} disabled={!userInput.trim()}>
                    重新解析
                </Button>,
                <Button key="cancel" onClick={handleClose}>
                    取消
                </Button>,
                <Button key="apply" type="primary" icon={<CheckOutlined />} onClick={handleApplyResult}>
                    应用到表单
                </Button>,
            ];
        }

        return [];
    };

    return (
        <Modal
            title={
                <Space>
                    <RobotOutlined />
                    {title}
                </Space>
            }
            open={visible}
            onCancel={handleClose}
            footer={renderFooter()}
            width={1200}
            maskClosable={false}
        >
            {step === "input" && renderInputStep()}
            {step === "processing" && renderProcessingStep()}
            {step === "result" && renderResultStep()}
        </Modal>
    );
};

export default AIFormFillModal;
